import { checkRefreshToken } from 'Api/ProfileApi';
import { pendingSignOut, signOut } from 'State/User';
import { createAuthInstance } from 'Utils/AuthSDK';
import { logError } from 'Utils/Logger';
import { createProfile } from 'Utils/ProfileSDK';
import { getUser, updateRefreshToken } from 'Utils/UserCookie';
import { getStoreState } from 'Utils/Store';

export const DEFAULT_MAX_API_RETRIES = 3;
export const DEFAULT_MAX_REFRESH_RETRIES = 3;

/* To prevent concurrent requests for new token. */
let reqInProgress = false;

class RefreshTokenHandler {
  currentApiRetry = 0;
  currentRefreshRetry = 0;
  maxApiRetries = DEFAULT_MAX_API_RETRIES;
  maxRefreshRetries = DEFAULT_MAX_REFRESH_RETRIES;

  init(requestCall) {
    this.requestCall = requestCall;
  }

  // borrowed back-off logic from axios-retry
  // https://github.com/softonic/axios-retry/blob/11c2cdbd2188d8085a7d84c6026e5cca3c24f070/es/index.mjs#L77
  getExponentialDelay(retryNum) {
    const delay = Math.pow(2, retryNum) * 100;
    const randomSum = delay * 0.4 * Math.random();
    return delay + randomSum;
  }

  parseStatusCode(err) {
    return err.statusCode || err.response?.status || err.code;
  }

  async makeRequest() {
    try {
      // if no error, return as normal so it's handled by our wrapper functions (e.g. in ProfileApi.js)
      return await this.requestCall();
    } catch (e) {
      // we only want to handle specifically the 401 error
      if (this.parseStatusCode(e) === 401) {
        if (++this.currentApiRetry <= this.maxApiRetries) {
          const user = getUser();
          if (!user) {
            this.logoutAndRedirectUser('User not logged in');
            return Promise.reject(new Error('User not logged in'));
          }
          if (reqInProgress) {
            await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxApiRetries)));
            return this.makeRequest();
          }
          let { refreshToken, accessTokenExpiration } = user;
          const success = await this.fetchNewToken(accessTokenExpiration, refreshToken);
          if (success) {
            await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxApiRetries)));
            return this.makeRequest();
          } else {
            // return generic message for refresh token failure
            return Promise.reject(new Error('Failure to refresh token'));
          }
        }
        this.logoutAndRedirectUser(new Error('Maximum api retries exceeded'));
        return Promise.reject(new Error('Maximum api retries exceeded'));
      }
      // if not 401, throw error as normal so our wrapper functions can handle it
      throw e;
    }
  }

  async fetchNewToken(accessTokenExpiration, refreshToken) {
    reqInProgress = true;
    try {
      const checkRefreshRes = await checkRefreshToken({ accessTokenExpiration, refreshToken });
      if (checkRefreshRes.refreshToken) {
        updateRefreshToken({
          refreshToken: checkRefreshRes.refreshToken,
          accessToken: checkRefreshRes.accessToken,
          accessTokenExpiration: checkRefreshRes.accessTokenExpiration,
        });
        createProfile();
        createAuthInstance();
      }
      reqInProgress = false;
      return true;
    } catch (error) {
      reqInProgress = false;
      return this.handleFetchTokenError(error);
    }
  }

  async handleFetchTokenError(error) {
    const statusCode = this.parseStatusCode(error);
    if (statusCode >= 400 && statusCode < 500) {
      this.logoutAndRedirectUser(new Error(error?.message || 'Failure to refresh token'));
      return false;
    }
    if (++this.currentRefreshRetry <= this.maxRefreshRetries) {
      await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxRefreshRetries)));
      return this.fetchNewToken();
    }
    this.logoutAndRedirectUser(new Error(error?.message || 'Maximum refresh token retries exceeded'));
    return false;
  }

  async logoutAndRedirectUser(error) {
    const user = getUser();
    // if user already logged out, do nothing
    if (getStoreState().user?.pendingSignOut || !user) return;
    logError(error);
    try {
      await pendingSignOut();
      await signOut();
    } catch (e) {
      console.error(e, 'Unable to sign out user');
    }
  }
}

export const createApiTokenHandler = (request) => {
  const tokenHandler = new RefreshTokenHandler();
  tokenHandler.init(request);
  return tokenHandler.makeRequest();
};
